import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  CaretDown, Plus, Trash, Buildings, User, CircleNotch, X, Check,
} from "@phosphor-icons/react";

const dot = (status) =>
  status === "connected" || status === "healthy" ? "bg-[#059669]"
    : status === "auth_error" || status === "error" ? "bg-[#DC2626]"
      : "bg-neutral-300";

export function IntegrationConnectionBar({ provider, value, onChange, onLoaded, label }) {
  const [conns, setConns] = useState([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [ownerType, setOwnerType] = useState("group");
  const [groups, setGroups] = useState([]);
  const [ownerId, setOwnerId] = useState("");
  const [clientQ, setClientQ] = useState("");
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/${provider}/connections`);
    setConns(data.connections || []);
    if (onLoaded) onLoaded(data.connections || []);
    if (!value && data.connections?.length) onChange(data.default_id || data.connections[0].id);
  }, [provider, value, onChange, onLoaded]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (adding && ownerType === "group" && groups.length === 0) api.get("/client-groups").then((r) => setGroups(r.data.groups || [])); }, [adding, ownerType, groups.length]);
  useEffect(() => {
    if (!adding || ownerType !== "client") return;
    const t = setTimeout(() => api.get("/clients", { params: { search: clientQ, limit: 15, status: "active" } }).then((r) => setClients(r.data.items || r.data.clients || [])), 350);
    return () => clearTimeout(t);
  }, [adding, ownerType, clientQ]);

  const current = conns.find((c) => c.id === value) || conns[0];

  const create = async () => {
    if (!ownerId) { toast.error("Select an owner first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/${provider}/connections`, { owner_type: ownerType, owner_id: ownerId });
      toast.success(`Connection added for ${data.owner_name}`);
      setAdding(false); setOwnerId(""); setClientQ("");
      await load();
      onChange(data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (conns.length <= 1) { toast.error("At least one connection must remain."); return; }
    if (!window.confirm("Delete this connection? Any cached data for it will be removed.")) return;
    try {
      await api.delete(`/${provider}/connections/${id}`);
      toast.success("Connection deleted");
      const rest = conns.filter((c) => c.id !== id);
      await load();
      if (value === id && rest[0]) onChange(rest[0].id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="mb-5 flex flex-wrap items-center gap-3 rounded-sm border border-neutral-200 bg-white p-3" data-testid={`${provider}-connection-bar`}>
      <span className="text-xs font-bold uppercase tracking-wider text-neutral-500">{label || "Client connection"}</span>
      <div className="relative">
        <button data-testid={`${provider}-conn-select`} onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-[#0A0A0A] hover:border-[#0055FF]">
          <span className={`h-2 w-2 rounded-full ${dot(current?.status)}`} />
          {current?.owner_name || "Select…"}
          {current?.is_default && <span className="rounded-sm bg-neutral-100 px-1.5 py-0.5 text-[10px] font-bold text-neutral-500">DEFAULT</span>}
          <CaretDown size={14} weight="bold" />
        </button>
        {open && (
          <div className="absolute z-20 mt-1 w-72 rounded-sm border border-neutral-200 bg-white py-1 shadow-lg" data-testid={`${provider}-conn-menu`}>
            {conns.map((c) => (
              <div key={c.id} className={`flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-neutral-50 ${c.id === value ? "bg-[#0055FF]/5" : ""}`}>
                <button onClick={() => { onChange(c.id); setOpen(false); }} className="flex flex-1 items-center gap-2 text-left" data-testid={`${provider}-conn-opt-${c.id}`}>
                  <span className={`h-2 w-2 rounded-full ${dot(c.status)}`} />
                  <span className="flex-1 truncate font-medium text-[#0A0A0A]">{c.owner_name}</span>
                  {c.id === value && <Check size={14} weight="bold" className="text-[#0055FF]" />}
                </button>
                <button onClick={() => remove(c.id)} className="text-neutral-300 hover:text-[#DC2626]" data-testid={`${provider}-conn-del-${c.id}`}><Trash size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button data-testid={`${provider}-conn-add`} onClick={() => setAdding(true)}
        className="inline-flex items-center gap-1.5 rounded-sm border border-dashed border-neutral-300 px-3 py-2 text-sm font-semibold text-neutral-600 hover:border-[#0055FF] hover:text-[#0055FF]">
        <Plus size={14} weight="bold" /> Add connection
      </button>

      {current?.owner_type && (
        <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-neutral-400">
          {current.owner_type === "group" ? <Buildings size={13} /> : <User size={13} />}
          {current.owner_type === "group" ? "Group (all sub-clients)" : "Single client"}
        </span>
      )}

      {adding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid={`${provider}-add-dialog`}>
          <div className="w-full max-w-md rounded-sm bg-white p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[#0A0A0A]">Add {provider.toUpperCase()} connection</h3>
              <button onClick={() => setAdding(false)}><X size={18} className="text-neutral-400" /></button>
            </div>
            <p className="mt-1 text-sm text-neutral-500">Choose which client this connection belongs to.</p>
            <div className="mt-4 flex gap-2">
              {["group", "client"].map((t) => (
                <button key={t} onClick={() => { setOwnerType(t); setOwnerId(""); }} data-testid={`${provider}-owner-type-${t}`}
                  className={`flex-1 rounded-sm border px-3 py-2 text-sm font-semibold ${ownerType === t ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-neutral-300 text-neutral-600"}`}>
                  {t === "group" ? "Client group" : "Single client"}
                </button>
              ))}
            </div>
            {ownerType === "group" ? (
              <select data-testid={`${provider}-owner-group`} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
                className="mt-3 w-full rounded-sm border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-[#0055FF]">
                <option value="">Select a client group…</option>
                {groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
              </select>
            ) : (
              <div className="mt-3">
                <input data-testid={`${provider}-owner-client-search`} value={clientQ} onChange={(e) => setClientQ(e.target.value)} placeholder="Search clients…"
                  className="w-full rounded-sm border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-[#0055FF]" />
                <div className="mt-2 max-h-44 overflow-y-auto rounded-sm border border-neutral-100">
                  {clients.map((c) => (
                    <button key={c.client_id || c.id} onClick={() => setOwnerId(c.client_id || c.id)} data-testid={`${provider}-owner-client-${c.client_id || c.id}`}
                      className={`block w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 ${ownerId === (c.client_id || c.id) ? "bg-[#0055FF]/5 font-semibold text-[#0055FF]" : ""}`}>
                      {c.name}
                    </button>
                  ))}
                  {clients.length === 0 && <p className="px-3 py-2 text-sm text-neutral-400">Type to search clients…</p>}
                </div>
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setAdding(false)} className="rounded-sm border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-600">Cancel</button>
              <button onClick={create} disabled={busy || !ownerId} data-testid={`${provider}-add-submit`}
                className="inline-flex items-center gap-1.5 rounded-sm bg-[#0055FF] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {busy ? <CircleNotch size={15} className="animate-spin" /> : <Plus size={15} weight="bold" />} Add connection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
